/**
 * The settings payload the Extension Settings tab receives. Every field is
 * optional because the backend genuinely omits them: a freshly-registered
 * account's `settings` document has no `fly_grammar_blocked_domains`,
 * `fly_rewrite_blocked_domains` or `csv_split_separator` at all, and
 * `loadSettingsFromData` is written to default each one. Marking them required
 * would only be true of an account that has already saved every card once.
 */
export interface ExtensionSettingsData {
  shortcut_timeout?: number;
  typing_speed?: number;
  typing_style?: 'word' | 'letter' | 'all';
  fly_grammar?: 'enabled' | 'disabled';
  fly_grammar_default_language?: string;
  fly_grammar_blocked_domains?: string[];
  fly_rewrite?: 'enabled' | 'disabled';
  fly_rewrite_blocked_domains?: string[];
  /**
   * Automatic Sentence Rewriter (revival, extension#370 / be#772). Default is
   * 'disabled' — unlike the other feature toggles, this one ships opt-in: the
   * feature sends typed text to FlyMSG's AI for scoring, so `undefined`/absent
   * must read as off, never on.
   */
  fly_sentence_rewrite?: 'enabled' | 'disabled';
  fly_sentence_rewrite_blocked_domains?: string[];
  blocked_domains?: string[];
  plugin_blocked_domains?: string[];
  csv_split_separator?: string | null;
  company?: CompanyOverrides;
}

/**
 * The five per-site blacklists in the user `settings` document. Key names are
 * the wire fields of `PUT v1/user/setting`; the feature → list mapping mirrors
 * the extension's canonical one (flymsg-extension
 * src/shared/dto/temporaryDisable.ts) so both surfaces write the same fields.
 */
export type DisabledSiteListKey =
  | 'blocked_domains'
  | 'fly_grammar_blocked_domains'
  | 'fly_rewrite_blocked_domains'
  | 'plugin_blocked_domains'
  | 'fly_sentence_rewrite_blocked_domains';

export type DisabledSiteFeature = 'shortcuts' | 'grammar' | 'rewrite' | 'plugin' | 'sentence';

export const FEATURE_LISTS: Record<DisabledSiteFeature, DisabledSiteListKey> = {
  shortcuts: 'blocked_domains',
  grammar: 'fly_grammar_blocked_domains',
  rewrite: 'fly_rewrite_blocked_domains',
  plugin: 'plugin_blocked_domains',
  sentence: 'fly_sentence_rewrite_blocked_domains',
};

export const ALL_DISABLED_SITE_LIST_KEYS: DisabledSiteListKey[] = [
  FEATURE_LISTS.shortcuts,
  FEATURE_LISTS.grammar,
  FEATURE_LISTS.rewrite,
  FEATURE_LISTS.plugin,
  FEATURE_LISTS.sentence,
];

export type DisabledSiteLists = Record<DisabledSiteListKey, string[]>;

export interface CompanyOverrides {
  override_user_typing_style?: boolean;
  typing_style?: string;
  typing_speed?: number;
  override_user_flygrammar?: boolean;
  fly_grammar?: string;
  fly_grammar_default_language?: string;
  override_user_flyrewrite?: boolean;
  fly_rewrite?: string;
  override_user_fly_sentence_rewrite?: boolean;
  fly_sentence_rewrite?: string;
}

export interface GrammarLanguage {
  id: string;
  value: string;
}

export interface MetaDataResponse {
  plan: ExtensionPlan;
  fly_grammar_languages_available: GrammarLanguage[];
}

export interface ExtensionPlan {
  title: string;
  identifier: string;
  stripe_id: string;
  has_fly_learning: boolean;
  user_custom_prompts: number;
  user_persona_available: number;
  flycut_deployment: number;
  flygrammar_actions: number;
  prompts_per_day: number;
  can_disable_flygrammar: boolean;
  regenerate_count: number;
  currency: string;
  interval: string;
  id: string;
}
